if (!customElements.get('product-compare-selector')) {
  /**
   * 模糊匹配：忽略大小写与空格
   * @param {string} text
   * @param {string} query
   */
  function matchesFuzzy(text, query) {
    const normalize = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');
    const needle = normalize(query);
    if (!needle) return true;
    return normalize(text).includes(needle);
  }

  class ProductCompareSelector extends HTMLElement {
    constructor() {
      super();
      this._onTriggerClick = this.onTriggerClick.bind(this);
      this._onSearchFocus = this.onSearchFocus.bind(this);
      this._onSearchInput = this.onSearchInput.bind(this);
      this._onSearchKeydown = this.onSearchKeydown.bind(this);
      this._onDocumentClick = this.onDocumentClick.bind(this);
      this._onCheckboxChange = this.onCheckboxChange.bind(this);
      this.selectedHandles = [];
      this.mainProductHandle = '';
      this.mainProductTitle = '';
      this.maxCompareSize = 4;
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-selector-trigger]');
      this.searchInput = this.querySelector('[data-selector-search]');
      this.dropdown = this.querySelector('[data-selector-dropdown]');
      this.noResultsEl = this.querySelector('[data-selector-no-results]');
      this.noResultsQueryEl = this.querySelector('[data-no-results-query]');
      this.addLabel = this.getAttribute('data-add-label') || 'Add products to compare';
      this.compareLabel = this.getAttribute('data-compare-label') || 'Compare';
      this.searchPlaceholder =
        this.getAttribute('data-search-placeholder') ||
        'Search and add products to compare';

      if (this.searchInput) {
        this.searchInput.placeholder = this.searchPlaceholder;
      }
      const labelEl = this.noResultsEl?.querySelector('[data-no-results-label]');
      if (labelEl) {
        labelEl.textContent = window.theme?.strings?.noResultsFor || 'No results for';
      }

      this.trigger?.addEventListener('click', this._onTriggerClick);
      this.searchInput?.addEventListener('focus', this._onSearchFocus);
      this.searchInput?.addEventListener('input', this._onSearchInput);
      this.searchInput?.addEventListener('keydown', this._onSearchKeydown);
      this.addEventListener('change', this._onCheckboxChange);
      document.addEventListener('click', this._onDocumentClick);
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this._onTriggerClick);
      this.searchInput?.removeEventListener('focus', this._onSearchFocus);
      this.searchInput?.removeEventListener('input', this._onSearchInput);
      this.searchInput?.removeEventListener('keydown', this._onSearchKeydown);
      this.removeEventListener('change', this._onCheckboxChange);
      document.removeEventListener('click', this._onDocumentClick);
      this.unlockActionsHeight();
    }

    /**
     * @param {{ mainProductHandle: string, mainProductTitle: string, selectedHandles?: string[], maxCompareSize?: number }} config
     */
    init(config) {
      this.mainProductHandle = config.mainProductHandle || '';
      this.mainProductTitle = config.mainProductTitle || '';
      if (config.maxCompareSize != null) {
        this.maxCompareSize = config.maxCompareSize;
      }
      this.selectedHandles = [...(config.selectedHandles || [])];

      this.querySelectorAll('[data-selector-checkbox]').forEach((input) => {
        const handle = input.value;
        const isMain = handle === this.mainProductHandle;
        const item = input.closest('[data-selector-item], .product-compare-selector__item');
        const checked = this.selectedHandles.includes(handle);
        input.checked = checked;

        if (isMain) {
          // 当前产品必选且占用序号 1，但不在下拉中展示，避免误导
          input.disabled = true;
          input.checked = true;
          if (!this.selectedHandles.includes(handle)) {
            this.selectedHandles.unshift(handle);
          } else if (this.selectedHandles[0] !== handle) {
            this.selectedHandles = [
              handle,
              ...this.selectedHandles.filter((itemHandle) => itemHandle !== handle),
            ];
          }
          item?.setAttribute('hidden', '');
          item?.classList.add('is-main-product');
        } else {
          item?.removeAttribute('hidden');
          item?.classList.remove('is-main-product');
        }
      });

      this.filterOptions();
      this.syncIndexes();
      this.dispatchSelectionChange();
    }

    onTriggerClick(e) {
      // 点击搜索图标/空白区域时聚焦输入框并展开
      if (e.target === this.searchInput) return;
      e.preventDefault();
      this.searchInput?.focus();
      this.openDropdown();
    }

    onSearchFocus() {
      this.openDropdown();
    }

    onSearchInput() {
      this.openDropdown();
      this.filterOptions();
    }

    onSearchKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDropdown();
        this.searchInput?.blur();
        return;
      }
      // 阻止 type=search 回车提交/刷新
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    }

    onDocumentClick(e) {
      if (!this.contains(e.target)) {
        this.closeDropdown();
      }
    }

    openDropdown() {
      if (!this.dropdown) return;
      if (this.classList.contains('is-open')) {
        this.searchInput?.setAttribute('aria-expanded', 'true');
        return;
      }
      this.classList.remove('is-closing');
      this.lockActionsHeight();
      this.dropdown.removeAttribute('hidden');
      // 双 rAF：先以 max-height:0 入流，再切到展开态，确保过渡生效
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.searchInput?.setAttribute('aria-expanded', 'true');
        });
      });
    }

    closeDropdown() {
      if (!this.dropdown) return;
      if (!this.classList.contains('is-open') || this.classList.contains('is-closing')) return;

      this.classList.remove('is-open');
      this.classList.add('is-closing');
      this.searchInput?.setAttribute('aria-expanded', 'false');

      const finishClose = () => {
        if (this.classList.contains('is-open')) return;
        this.classList.remove('is-closing');
        this.dropdown.setAttribute('hidden', '');
        this.unlockActionsHeight();
        this.dropdown.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(fallbackTimer);
      };
      const onTransitionEnd = (e) => {
        if (e.target !== this.dropdown) return;
        if (e.propertyName !== 'max-height') return;
        finishClose();
      };
      const fallbackTimer = setTimeout(finishClose, 450);
      this.dropdown.addEventListener('transitionend', onTransitionEnd);
    }

    /**
     * 展开前锁定 actions 布局高度，避免下拉撑高后续内容；overflow:visible 仍可叠出
     */
    lockActionsHeight() {
      const actions =
        this.closest('.product-compare-dialog__actions') ||
        this.closest('[data-compare-dialog-ajax-content]')?.querySelector('.product-compare-dialog__actions');
      this.actionsEl = actions || null;
      if (!this.actionsEl) return;
      this.actionsEl.style.maxHeight = `${this.actionsEl.offsetHeight}px`;
    }

    unlockActionsHeight() {
      if (!this.actionsEl) return;
      this.actionsEl.style.maxHeight = '';
      this.actionsEl = null;
    }

    /**
     * 按搜索词筛选列表（当前产品始终隐藏）
     */
    filterOptions() {
      const query = this.searchInput?.value || '';
      const trimmedQuery = query.trim();
      let visibleCount = 0;

      this.querySelectorAll('[data-selector-item], .product-compare-selector__item').forEach((item) => {
        const option = item.querySelector('.product-compare-selector__option, [data-selector-option]');
        const handle = option?.getAttribute('data-product-handle') || '';
        if (handle === this.mainProductHandle || item.classList.contains('is-main-product')) {
          item.setAttribute('hidden', '');
          return;
        }
        const title = option?.getAttribute('data-product-title') || option?.textContent || '';
        if (matchesFuzzy(title, query)) {
          item.removeAttribute('hidden');
          visibleCount += 1;
        } else {
          item.setAttribute('hidden', '');
        }
      });

      const showNoResults = Boolean(trimmedQuery) && visibleCount === 0;
      if (this.noResultsEl) {
        if (showNoResults) {
          if (this.noResultsQueryEl) {
            this.noResultsQueryEl.textContent = trimmedQuery;
          }
          this.noResultsEl.removeAttribute('hidden');
        } else {
          this.noResultsEl.setAttribute('hidden', '');
        }
      }
    }

    onCheckboxChange(e) {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-selector-checkbox]')) return;

      const handle = input.value;
      if (handle === this.mainProductHandle) {
        input.checked = true;
        return;
      }

      const scrollRoot = this.dropdown;
      const option = input.closest('.product-compare-selector__option, [data-selector-option]');
      const scrollTop = scrollRoot?.scrollTop ?? 0;

      if (input.checked) {
        if (this.selectedHandles.length >= this.maxCompareSize) {
          input.checked = false;
          return;
        }
        if (!this.selectedHandles.includes(handle)) {
          this.selectedHandles.push(handle);
        }
      } else {
        this.selectedHandles = this.selectedHandles.filter((item) => item !== handle);
      }

      this.syncIndexes();
      this.dispatchSelectionChange();

      // 先抵消 focus 导致的瞬间跳动，再平滑滚到点击项可见
      if (scrollRoot) {
        scrollRoot.scrollTop = scrollTop;
        requestAnimationFrame(() => {
          scrollRoot.scrollTop = scrollTop;
          this.scrollOptionIntoView(option, scrollRoot);
        });
      }
    }

    /**
     * 将选项平滑滚入下拉列表可视区域
     * @param {Element | null} option
     * @param {HTMLElement | null} scrollRoot
     * @param {number} [duration=280]
     */
    scrollOptionIntoView(option, scrollRoot, duration = 280) {
      if (!option || !scrollRoot) return;

      const rootRect = scrollRoot.getBoundingClientRect();
      const optionRect = option.getBoundingClientRect();
      let delta = 0;

      if (optionRect.top < rootRect.top) {
        delta = optionRect.top - rootRect.top;
      } else if (optionRect.bottom > rootRect.bottom) {
        delta = optionRect.bottom - rootRect.bottom;
      }

      if (!delta) return;

      const from = scrollRoot.scrollTop;
      const to = from + delta;
      const start = performance.now();
      const easeOutCubic = (t) => 1 - (1 - t) ** 3;

      if (this._scrollIntoViewRaf) {
        cancelAnimationFrame(this._scrollIntoViewRaf);
      }

      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        scrollRoot.scrollTop = from + (to - from) * easeOutCubic(t);
        if (t < 1) {
          this._scrollIntoViewRaf = requestAnimationFrame(step);
        } else {
          this._scrollIntoViewRaf = 0;
        }
      };

      this._scrollIntoViewRaf = requestAnimationFrame(step);
    }

    syncIndexes() {
      this.querySelectorAll('[data-selector-option], .product-compare-selector__option').forEach((option) => {
        const input = option.querySelector('[data-selector-checkbox]');
        const indexEl = option.querySelector('[data-compare-index]');
        if (!input || !indexEl) return;
        const index = this.selectedHandles.indexOf(input.value);
        if (index > -1 && input.checked) {
          indexEl.textContent = String(index + 1);
          option.classList.add('is-selected');
        } else {
          indexEl.textContent = '';
          option.classList.remove('is-selected');
        }
      });
    }

    dispatchSelectionChange() {
      this.dispatchEvent(
        new CustomEvent('theme:product-compare:selection-change', {
          bubbles: true,
          detail: {
            selectedHandles: [...this.selectedHandles],
          },
        }),
      );
    }
  }

  customElements.define('product-compare-selector', ProductCompareSelector);
}

if (!customElements.get('product-compare-dialog')) {
  customElements.define(
    'product-compare-dialog',
    class ProductCompareDialog extends window.theme.modules.CommonPopup {
      constructor() {
        super();

        this.productType = this.getAttribute('data-product-type') || '';
        this.mainProductHandle = this.getAttribute('data-main-product-handle') || '';
        this.mainProductTitle = this.getAttribute('data-main-product-title') || '';
        this.maxCompareSize = this.parseMaxCompareSize(this.getAttribute('data-max-compare-size'));

        this.renderElement = this.querySelector('[data-compare-dialog-content-render]');
        this.ajaxContent = this.querySelector('[data-compare-dialog-ajax-content]');
        this.retryButton = this.querySelector('[data-compare-dialog-ajax-retry]');

        this._reload = this.reload.bind(this);
        this._onSelectionChange = this.onSelectionChange.bind(this);
        this._onStickyCheck = this.checkStickyState.bind(this);
        this._onTableWrapScroll = this.onTableWrapScroll.bind(this);
        this._onScrollPrevClick = this.onScrollPrevClick.bind(this);
        this._onScrollNextClick = this.onScrollNextClick.bind(this);
        this._onUpdateTableScrollButtons = this.updateTableScrollButtons.bind(this);
        this._onEqualizeCompareCards = this.equalizeCompareCardHeights.bind(this);
        this._onUnitSwitchClick = this.onUnitSwitchClick.bind(this);

        this.initialized = false;
        this.compareMeta = { size_map: {}, material_map: {}, translations: {}, default_unit: 'imperial' };
        this.productTemplates = new Map();
        this.cachedResponseText = new Map();
        this.cachedProducts = new Map();
        this.currentUnit = null;
        this._loadedProductType = null;
        this._boundMainHandle = null;
        this._reloadId = 0;
        this._openId = 0;

        if (this.hasAttribute('open') && this.getAttribute('open') != 'false') {
          this.open();
        }
      }

      /** @type {'init' | 'loading' | 'error' | 'success'} */
      get loadingState() {
        return this.getAttribute('data-loading-state');
      }

      set loadingState(state) {
        this.setAttribute('data-loading-state', state);
      }

      /**
       * 解析 data-max-compare-size，支持数字与 Infinity
       * @param {string | null} value
       * @param {number} [fallback=4]
       */
      parseMaxCompareSize(value, fallback = 4) {
        if (value == null || value === '') return fallback;
        const raw = String(value).trim();
        if (/^infinity$/i.test(raw)) return Infinity;
        const num = Number(raw);
        return Number.isFinite(num) && num > 0 ? num : fallback;
      }

      connectedCallback() {
        super.connectedCallback();
        this.retryButton?.addEventListener('click', this._reload);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.retryButton?.removeEventListener('click', this._reload);
        this.selector?.removeEventListener('theme:product-compare:selection-change', this._onSelectionChange);
        this.teardownStickyObserver();
        this.teardownTableScrollButtons();
        this.teardownTableDragScroll();
        this.teardownCompareCardHeightSync();
      }

      /**
       * 打开弹窗。传入 handle 时复用同一个弹窗加载对应产品。
       * @param {string} [productHandle]
       */
      open(productHandle) {
        super.open();
        this.setAttribute('open', 'true');
        document.dispatchEvent(
          new CustomEvent('theme:scroll:lock', { bubbles: true, detail: this.renderElement }),
        );
        this.prepareAndLoad(productHandle);
      }

      /**
       * @param {string} [productHandle]
       */
      async prepareAndLoad(productHandle) {
        const token = ++this._openId;
        try {
          if (productHandle) {
            await this.applyMainProduct(productHandle);
          }
          if (token !== this._openId) return;
          if (!this.productType || !this.mainProductHandle) {
            throw new Error('ProductCompareDialog: Missing product handle or type');
          }

          const typeChanged = this._loadedProductType !== this.productType;
          if (!this.initialized || typeChanged) {
            await this.reload();
            return;
          }

          if (this._boundMainHandle !== this.mainProductHandle) {
            this.bindContent();
          }
        } catch (error) {
          console.error('[ProductCompareDialog]', error);
          this.showErrorDetail(error);
          this.loadingState = 'error';
        }
      }

      /**
       * @param {string} handle
       */
      async applyMainProduct(handle) {
        const product = await this.fetchProductJson(handle);
        this.mainProductHandle = product.handle || handle;
        this.mainProductTitle = product.title || handle;
        this.productType = product.product_type || product.type || '';
        this.setAttribute('data-main-product-handle', this.mainProductHandle);
        this.setAttribute('data-main-product-title', this.mainProductTitle);
        this.setAttribute('data-product-type', this.productType);
      }

      /**
       * @param {string} handle
       */
      async fetchProductJson(handle) {
        if (this.cachedProducts.has(handle)) {
          return this.cachedProducts.get(handle);
        }
        const response = await fetch(`${window.theme.routes.root_url}products/${handle}.js`);
        if (!response.ok) {
          const error = new Error(`Product fetch failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        const json = await response.json();
        this.cachedProducts.set(handle, json);
        return json;
      }

      async reload() {
        if (!this.productType) return;
        const token = ++this._reloadId;
        this.loadingState = 'loading';
        this.clearErrorDetail();

        try {
          await this.fetchContent();
          if (token !== this._reloadId) return;
          this.loadingState = 'success';
          this.initialized = true;
          this._loadedProductType = this.productType;
          this.bindContent();
        } catch (error) {
          if (token !== this._reloadId) return;
          this.cachedResponseText.delete(this.productType);
          console.error('[ProductCompareDialog]', error);
          this.showErrorDetail(error);
          this.loadingState = 'error';
        }
      }

      async fetchContent() {
        const productType = this.productType || '';
        let html = this.cachedResponseText.get(productType);

        if (!html) {
          const url =
            window.Shopify.routes.root +
            `collections/all-products?filter.p.product_type=${encodeURIComponent(productType)}&section_id=api-product-compare-dialog-content`;

          const response = await fetch(url);
          if (!response.ok) {
            const error = new Error(`Compare dialog fetch failed: ${response.status}`);
            error.status = response.status;
            throw error;
          }
          html = await response.text();
          this.cachedResponseText.set(productType, html);
        }

        const fresh = document.createElement('div');
        fresh.innerHTML = html;
        const fetchContent = fresh.querySelector('[data-compare-dialog-ajax-content]');
        this.ajaxContent = this.ajaxContent || this.querySelector('[data-compare-dialog-ajax-content]');
        if (!fetchContent || !this.ajaxContent) {
          throw new Error('ProductCompareDialog: Ajax api not found content');
        }

        this.teardownStickyObserver();
        this.teardownTableScrollButtons();
        this.teardownTableDragScroll();
        this.teardownCompareCardHeightSync();
        this.selector?.removeEventListener('theme:product-compare:selection-change', this._onSelectionChange);
        this.ajaxContent.innerHTML = fetchContent.innerHTML;
      }

      close() {
        super.close();
        document.dispatchEvent(
          new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: this.renderElement }),
        );
        this.removeAttribute('open');
      }

      clearErrorDetail() {
        const detailEl = this.querySelector('[data-compare-dialog-error-detail]');
        if (!detailEl) return;
        detailEl.textContent = '';
        detailEl.classList.add('hide');
      }

      /**
       * @param {Error & { status?: number }} error
       */
      showErrorDetail(error) {
        const detailEl = this.querySelector('[data-compare-dialog-error-detail]');
        if (!detailEl) return;

        const status = error?.status;
        const detail =
          (typeof window.theme?.methods?.handleFetchErrorMessage === 'function'
            ? window.theme.methods.handleFetchErrorMessage(status)
            : '') || '';

        if (detail) {
          detailEl.textContent = detail;
          detailEl.classList.remove('hide');
        } else {
          detailEl.textContent = '';
          detailEl.classList.add('hide');
        }
      }

      bindContent() {
        this.selector = this.querySelector('product-compare-selector');
        this.tableContainer = this.querySelector('[data-compare-table-container]');
        this.tableScroll = this.querySelector('[data-compare-table-scroll]');
        this.tableWrap = this.querySelector('[data-compare-table-wrap]');
        this.table = this.querySelector('[data-compare-table]');
        this.theadRow = this.querySelector('[data-compare-product-row]');
        this.tbody = this.querySelector('[data-compare-tbody]');
        this.stickyWrapper = this.querySelector('[data-compare-sticky]');
        this.stickyInner = this.querySelector('[data-compare-sticky-inner]');
        this.stickyScroll = this.querySelector('[data-compare-sticky-scroll]');
        this.stickyRow = this.querySelector('[data-compare-sticky-row]');
        this.scrollPrevBtn = this.querySelector('[data-compare-scroll-prev]');
        this.scrollNextBtn = this.querySelector('[data-compare-scroll-next]');

        const metaEl = this.querySelector('[data-compare-meta]');
        try {
          this.compareMeta = JSON.parse(metaEl?.textContent || '{}');
        } catch (error) {
          console.error('[ProductCompareDialog] meta parse error', error);
          this.compareMeta = { size_map: {}, material_map: {}, translations: {} };
        }

        this.productTemplates = new Map();
        this.querySelectorAll('[data-compare-product-template]').forEach((template) => {
          const handle = template.getAttribute('data-compare-product-template');
          if (handle) this.productTemplates.set(handle, template);
        });

        const allHandles = [...this.productTemplates.keys()];
        const otherHandle = allHandles.find((handle) => handle !== this.mainProductHandle);
        const initialHandles = [this.mainProductHandle, otherHandle].filter(Boolean);

        this.setupStickyObserver();
        this.setupTableScrollButtons();
        this.setupTableDragScroll();
        this.setupCompareCardHeightSync();

        // 选择器可选：有则接管勾选；无则直接用初始产品渲染表格（取消注释后自动恢复）
        this.selector?.removeEventListener('theme:product-compare:selection-change', this._onSelectionChange);
        if (this.selector) {
          this.selector.addEventListener('theme:product-compare:selection-change', this._onSelectionChange);
          this.selector.init({
            mainProductHandle: this.mainProductHandle,
            mainProductTitle: this.mainProductTitle,
            selectedHandles: initialHandles,
            maxCompareSize: this.maxCompareSize,
          });
        } else {
          this.renderCompareTable(initialHandles);
        }
        this._boundMainHandle = this.mainProductHandle;
      }

      setupStickyObserver() {
        this.teardownStickyObserver();
        if (!this.renderElement || !this.theadRow || !this.stickyWrapper) return;

        this.renderElement.addEventListener('scroll', this._onStickyCheck, { passive: true });
        this.tableWrap?.addEventListener('scroll', this._onTableWrapScroll, { passive: true });
        window.addEventListener('resize', this._onStickyCheck);
        this.checkStickyState();
      }

      teardownStickyObserver() {
        this.renderElement?.removeEventListener('scroll', this._onStickyCheck);
        this.tableWrap?.removeEventListener('scroll', this._onTableWrapScroll);
        window.removeEventListener('resize', this._onStickyCheck);
        this.stickyInner?.classList.remove('is-active');
        if (this.stickyWrapper) this.stickyWrapper.style.top = '';
      }

      setupTableScrollButtons() {
        this.teardownTableScrollButtons();
        this.scrollPrevBtn?.addEventListener('click', this._onScrollPrevClick);
        this.scrollNextBtn?.addEventListener('click', this._onScrollNextClick);
        window.addEventListener('resize', this._onUpdateTableScrollButtons);
        this.updateTableScrollButtons();
      }

      teardownTableScrollButtons() {
        this.scrollPrevBtn?.removeEventListener('click', this._onScrollPrevClick);
        this.scrollNextBtn?.removeEventListener('click', this._onScrollNextClick);
        window.removeEventListener('resize', this._onUpdateTableScrollButtons);
      }

      setupTableDragScroll() {
        this.teardownTableDragScroll();

        const DragSnapScroll = window.theme?.modules?.DragSnapScroll;
        if (!this.tableWrap || !DragSnapScroll) return;

        this.tableDragScroll = new DragSnapScroll(this.tableWrap, {
          getSnapStep: () => this.getTableScrollStep(),
        });
        this.tableDragScroll.bind();
      }

      teardownTableDragScroll() {
        this.tableDragScroll?.destroy();
        this.tableDragScroll = null;
      }

      /**
       * 单列步进宽度（用于按钮翻页与 snap）
       */
      getTableScrollStep() {
        if (!this.tableWrap) return 200;
        const cell = this.tableWrap.querySelector(
          '.product-compare-dialog__product, .product-compare-dialog__value, [data-compare-product]',
        );
        return cell?.offsetWidth || Math.max(this.tableWrap.clientWidth * 0.8, 200);
      }

      onTableWrapScroll() {
        this.syncStickyScrollFromTable();
        this.updateTableScrollButtons();
      }

      onScrollPrevClick(e) {
        e.preventDefault();
        this.selector?.closeDropdown?.();
        this.scrollTableBy(-1);
      }

      onScrollNextClick(e) {
        e.preventDefault();
        this.selector?.closeDropdown?.();
        this.scrollTableBy(1);
      }

      /**
       * @param {number} direction -1 左 / 1 右
       */
      scrollTableBy(direction) {
        if (this.tableDragScroll) {
          this.tableDragScroll.scrollByStep(direction);
          return;
        }
        if (!this.tableWrap) return;
        this.tableWrap.scrollBy({
          left: direction * this.getTableScrollStep(),
          behavior: 'smooth',
        });
      }

      updateTableScrollButtons() {
        if (!this.tableWrap) return;
        const maxScroll = this.tableWrap.scrollWidth - this.tableWrap.clientWidth;
        const canScroll = maxScroll > 2;
        const atStart = this.tableWrap.scrollLeft <= 2;
        const atEnd = this.tableWrap.scrollLeft >= maxScroll - 2;

        this.scrollPrevBtn?.classList.toggle('is-hidden', !canScroll || atStart);
        this.scrollNextBtn?.classList.toggle('is-hidden', !canScroll || atEnd);
      }

      setupCompareCardHeightSync() {
        this.teardownCompareCardHeightSync();
        window.addEventListener('resize', this._onEqualizeCompareCards);
        this.equalizeCompareCardHeights();
      }

      teardownCompareCardHeightSync() {
        window.removeEventListener('resize', this._onEqualizeCompareCards);
        if (this._equalizeCardsRaf) {
          cancelAnimationFrame(this._equalizeCardsRaf);
          this._equalizeCardsRaf = 0;
        }
      }

      /**
       * 统一 thead 内各 data-compare-card 高度（table 无法用 CSS 拉齐跨单元格内容）
       */
      equalizeCompareCardHeights() {
        if (!this.theadRow) return;
        const cards = [...this.theadRow.querySelectorAll('[data-compare-card]')];
        if (!cards.length) return;

        cards.forEach((card) => {
          card.style.minHeight = '';
        });

        if (cards.length === 1) return;

        const maxHeight = Math.max(...cards.map((card) => card.getBoundingClientRect().height));
        if (!maxHeight) return;

        cards.forEach((card) => {
          card.style.minHeight = `${Math.ceil(maxHeight)}px`;
        });
      }

      /**
       * 渲染后 / 图片加载后重新对齐卡片高度
       */
      scheduleEqualizeCompareCardHeights() {
        this.equalizeCompareCardHeights();
        if (this._equalizeCardsRaf) cancelAnimationFrame(this._equalizeCardsRaf);
        this._equalizeCardsRaf = requestAnimationFrame(() => {
          this.equalizeCompareCardHeights();
          this._equalizeCardsRaf = requestAnimationFrame(() => this.equalizeCompareCardHeights());
        });

        this.theadRow?.querySelectorAll('[data-compare-card] img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', this._onEqualizeCompareCards, { once: true });
          img.addEventListener('error', this._onEqualizeCompareCards, { once: true });
        });
      }

      /**
       * 参考 useTableStickyScrollObserver：产品行滚出可视区后显示吸顶标题栏；
       * 接近表格容器底部时上推收起。
       */
      checkStickyState() {
        if (!this.renderElement || !this.theadRow || !this.stickyWrapper || !this.stickyInner) return;
        if (!this.tableContainer) return;

        const viewportRect = this.renderElement.getBoundingClientRect();
        const viewportHeight = viewportRect.height;
        const targetRect = this.theadRow.getBoundingClientRect();
        const containerRect = this.tableContainer.getBoundingClientRect();
        const stickyHeight = this.stickyInner.offsetHeight || 0;

        // sticky 相对弹窗滚动容器顶部的位置（贴顶时约为 0）
        const stickyTop = Math.max(0, this.stickyWrapper.getBoundingClientRect().top - viewportRect.top);
        const stickyBottom = viewportHeight - stickyTop - stickyHeight;

        // target（产品卡片行）状态
        let targetStatus = 'in';
        const targetBottomToViewport = viewportHeight - (targetRect.bottom - viewportRect.top);
        if (targetBottomToViewport > stickyBottom) {
          targetStatus = 'top';
        } else if (targetRect.top - viewportRect.top >= viewportHeight) {
          targetStatus = 'bottom';
        }

        // container（整块表格区域）状态
        let containerStatus = 'in';
        const containerBottomToViewport = viewportHeight - (containerRect.bottom - viewportRect.top);
        if (containerBottomToViewport >= viewportHeight - stickyTop) {
          containerStatus = 'top';
        } else if (containerRect.top - viewportRect.top >= viewportHeight) {
          containerStatus = 'bottom';
        }

        // 接近容器底部时上推吸顶栏
        if (containerBottomToViewport > viewportHeight - stickyHeight - stickyTop) {
          this.stickyWrapper.style.top = `${viewportHeight - containerBottomToViewport - stickyHeight}px`;
        } else {
          this.stickyWrapper.style.top = '';
        }

        if (containerStatus !== 'in') {
          this.stickyInner.classList.remove('is-active');
          return;
        }

        if (targetStatus === 'top') {
          this.stickyInner.classList.add('is-active');
        } else {
          this.stickyInner.classList.remove('is-active');
        }
      }

      syncStickyScrollFromTable() {
        if (!this.tableWrap || !this.stickyScroll) return;
        this.stickyScroll.scrollLeft = this.tableWrap.scrollLeft;
      }

      /**
       * @param {Array<{ handle: string, title: string }>} products
       */
      renderStickyTitles(products) {
        if (!this.stickyRow) return;

        this.stickyRow.innerHTML = '';
        const corner = document.createElement('div');
        corner.className = 'product-compare-dialog__sticky-corner';
        corner.setAttribute('aria-hidden', 'true');
        this.stickyRow.appendChild(corner);

        products.forEach((product, index) => {
          const cell = document.createElement('div');
          cell.className = 'product-compare-dialog__sticky-title';
          cell.dataset.productHandle = product.handle;
          if (index === 0) cell.classList.add('product-compare-dialog__sticky-title--first');
          cell.textContent = product.title;
          this.stickyRow.appendChild(cell);
        });

        this.tableContainer?.style.setProperty('--columns', String(products.length));
        this.tableContainer?.setAttribute('data-columns', String(products.length));
        this.syncStickyScrollFromTable();
        requestAnimationFrame(() => this.checkStickyState());
      }

      onSelectionChange(e) {
        const selectedHandles = e.detail?.selectedHandles || [];
        this.renderCompareTable(selectedHandles);
      }

      /**
       * @param {string[]} selectedHandles
       */
      renderCompareTable(selectedHandles) {
        if (!this.theadRow || !this.tbody) return;

        const products = selectedHandles
          .map((handle) => this.getProductData(handle))
          .filter(Boolean);

        // thead: corner + product cards（复用已存在列，避免图片重载闪烁）
        this.reconcileProductCards(products);

        // tbody rows
        this.tbody.innerHTML = '';
        const sizeMap = this.compareMeta.size_map || {};
        const materialMap = this.compareMeta.material_map || {};
        const translations = this.compareMeta.translations || {};
        const sizeGroups = this.normalizeSizeMapGroups(sizeMap);
        const materialKeys = this.collectParamKeys(products, 'material', materialMap, {
          appendMissing: true,
        });
        const productCount = products.length;

        const sizeGroupKeyLists = sizeGroups.map((groupMap) =>
          this.collectParamKeys(products, 'size', groupMap, {appendMissing: false}),
        );
        const mappedSizeKeys = new Set(sizeGroupKeyLists.flat());
        const orphanSizeKeys = this.collectParamKeys(products, 'size', {}, {appendMissing: true}).filter(
          (key) => !mappedSizeKeys.has(key),
        );
        const hasSizeRows = sizeGroupKeyLists.some((keys) => keys.length > 0) || orphanSizeKeys.length > 0;

        if (hasSizeRows) {
          this.tbody.appendChild(this.buildUnitSwitchRow(productCount));
        }

        sizeGroupKeyLists.forEach((keys, groupIndex) => {
          keys.forEach((key) => {
            const convertible = key !== 'laptop';
            const labelMap = sizeGroups[groupIndex] || {};
            this.tbody.appendChild(
              this.buildParamRow(
                labelMap[key] || key,
                products,
                (product) => product.size?.[key],
                false,
                convertible,
              ),
            );
          });

          const laterHasKeys =
            sizeGroupKeyLists.slice(groupIndex + 1).some((list) => list.length > 0) ||
            orphanSizeKeys.length > 0;
          if (keys.length > 0 && laterHasKeys) {
            this.tbody.appendChild(this.buildSpacerRow(productCount));
          }
        });

        orphanSizeKeys.forEach((key) => {
          const convertible = key !== 'laptop';
          this.tbody.appendChild(
            this.buildParamRow(key, products, (product) => product.size?.[key], false, convertible),
          );
        });

        if (hasSizeRows) {
          this.tbody.appendChild(this.buildSpacerRow(productCount));
        }

        const keyFeaturesRow = this.buildParamRow(
          translations.key_features || 'Key Features',
          products,
          (product) => product.keyFeaturesHtml,
          true,
        );
        keyFeaturesRow.setAttribute('data-compare-key-features-row', '');
        this.tbody.appendChild(keyFeaturesRow);

        if (materialKeys.length > 0) {
          this.tbody.appendChild(this.buildSpacerRow(productCount));
          this.tbody.appendChild(
            this.buildMaterialMergedRow(
              translations.material || 'Material',
              products,
              materialMap,
              materialKeys,
            ),
          );
        }

        this.table?.style.setProperty('--columns', String(products.length));
        this.table?.setAttribute('data-columns', String(products.length));

        this.renderStickyTitles(products);
        this.applyUnit(this.getActiveUnit());
        requestAnimationFrame(() => this.updateTableScrollButtons());
        this.scheduleEqualizeCompareCardHeights();
      }

      /**
       * 按选中产品顺序同步 thead 产品列：保留未变更列的 DOM（避免图片重载闪烁）
       * @param {Array<object>} products
       */
      reconcileProductCards(products) {
        if (!this.theadRow) return;

        const desiredHandles = products.map((product) => product.handle);
        const existingByHandle = new Map();
        this.theadRow.querySelectorAll('th.product-compare-dialog__product[data-product-handle]').forEach((th) => {
          existingByHandle.set(th.dataset.productHandle, th);
        });

        // 先移除取消勾选的列
        existingByHandle.forEach((th, handle) => {
          if (!desiredHandles.includes(handle)) {
            th.remove();
            existingByHandle.delete(handle);
          }
        });

        let corner = this.theadRow.querySelector('th.product-compare-dialog__corner');
        if (!corner) {
          corner = document.createElement('th');
          corner.className = 'product-compare-dialog__corner';
          corner.scope = 'col';
          this.theadRow.prepend(corner);
        } else if (this.theadRow.firstElementChild !== corner) {
          this.theadRow.prepend(corner);
        }

        products.forEach((product, index) => {
          const targetIndex = index + 1; // 0 = corner
          let th = existingByHandle.get(product.handle);
          const isNew = !th;

          if (!th) {
            th = document.createElement('th');
            th.className = 'product-compare-dialog__product';
            th.scope = 'col';
            th.dataset.productHandle = product.handle;
            const card = product.cardNode.cloneNode(true);
            card.querySelectorAll('img').forEach((img) => {
              img.loading = 'eager';
            });
            th.appendChild(card);
          }

          const currentChild = this.theadRow.children[targetIndex];
          if (currentChild !== th) {
            if (currentChild) this.theadRow.insertBefore(th, currentChild);
            else this.theadRow.appendChild(th);
          }

          if (isNew) {
            th.querySelectorAll('product-grid-item-v3').forEach((el) => {
              if (typeof el.connectedCallback === 'function') el.connectedCallback();
            });
          }
        });
      }

      getDefaultUnit() {
        return (
          this.compareMeta?.default_unit ||
          window.theme?.settings?.product_default_unit ||
          'imperial'
        );
      }

      getActiveUnit() {
        return this.currentUnit || this.getDefaultUnit();
      }

      /**
       * 分组之间的空隙行：左侧 param + 右侧 colspan 产品列（移动端隐藏 param 后列数对齐）
       * @param {number} productCount
       */
      buildSpacerRow(productCount) {
        const tr = document.createElement('tr');
        tr.className = 'product-compare-dialog__spacer-row';

        const first = document.createElement('th');
        first.scope = 'row';
        first.className = 'product-compare-dialog__param product-compare-dialog__spacer-cell';
        first.setAttribute('aria-hidden', 'true');
        tr.appendChild(first);

        const td = document.createElement('td');
        td.colSpan = Math.max(productCount, 1);
        td.className = 'product-compare-dialog__value product-compare-dialog__spacer-cell';
        td.setAttribute('aria-hidden', 'true');
        tr.appendChild(td);

        return tr;
      }

      /**
       * 单位切换行：与参数行同列结构（1 param + N value），不用 colspan。
       * 按钮桌面放 param 列，移动端放首个产品列，保证双端可见列数一致。
       * @param {number} productCount
       */
      buildUnitSwitchRow(productCount) {
        const defaultUnit = this.getDefaultUnit();
        const activeUnit = this.getActiveUnit();
        const translations = this.compareMeta.translations || {};
        const imperialLabel = translations.imperial || 'Imperial';
        const metricLabel = translations.metric || 'Metric';

        const buttonOrder =
          defaultUnit === 'imperial'
            ? [
                { unit: 'imperial', label: imperialLabel },
                { unit: 'metric', label: metricLabel },
              ]
            : [
                { unit: 'metric', label: metricLabel },
                { unit: 'imperial', label: imperialLabel },
              ];

        const createSwitch = (modifierClass) => {
          const wrap = document.createElement('div');
          wrap.className = `product-compare-dialog__unit-switch product-specifications__header-buttons ${modifierClass}`;
          wrap.setAttribute('data-default-unit', defaultUnit);

          buttonOrder.forEach(({ unit, label }) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = `link-underline-hover link-underline${unit === activeUnit ? ' active' : ''}`;
            button.setAttribute('data-unit-switch', unit);
            button.textContent = label;
            button.addEventListener('click', this._onUnitSwitchClick);
            wrap.appendChild(button);
          });

          return wrap;
        };

        const tr = document.createElement('tr');
        tr.className = 'product-compare-dialog__unit-row';

        const paramCell = document.createElement('th');
        paramCell.scope = 'row';
        paramCell.className = 'product-compare-dialog__param product-compare-dialog__unit-cell';
        paramCell.appendChild(createSwitch('product-compare-dialog__unit-switch--desktop'));
        tr.appendChild(paramCell);

        for (let i = 0; i < productCount; i++) {
          const td = document.createElement('td');
          td.className = 'product-compare-dialog__value product-compare-dialog__unit-cell';
          if (i === 0) {
            td.appendChild(createSwitch('product-compare-dialog__unit-switch--mobile'));
          }
          tr.appendChild(td);
        }

        return tr;
      }

      onUnitSwitchClick(e) {
        const button = e.currentTarget;
        const unit = button?.getAttribute('data-unit-switch');
        if (!unit) return;
        this.applyUnit(unit);
      }

      /**
       * @param {string} unit
       */
      applyUnit(unit) {
        if (!unit || !this.tbody) return;
        this.currentUnit = unit;

        this.tbody.querySelectorAll('[data-unit-switch]').forEach((button) => {
          button.classList.toggle('active', button.getAttribute('data-unit-switch') === unit);
        });

        const convertUnit = window.theme?.methods?.convertUnit;
        this.tbody.querySelectorAll('[data-compare-convertible]').forEach((el) => {
          const original = el.getAttribute('data-original-html');
          if (original == null) return;
          if (typeof convertUnit === 'function') {
            el.innerHTML = convertUnit(original, unit);
          } else {
            el.innerHTML = original;
          }
        });
      }

      /**
       * @param {string} handle
       */
      getProductData(handle) {
        const template = this.productTemplates.get(handle);
        if (!template) return null;

        const root = template.content.querySelector('[data-compare-product]');
        if (!root) return null;

        let size = {};
        let material = {};
        try {
          size = JSON.parse(root.getAttribute('data-specs-size') || '{}') || {};
        } catch (_) {
          size = {};
        }
        try {
          material = JSON.parse(root.getAttribute('data-specs-material') || '{}') || {};
        } catch (_) {
          material = {};
        }

        const cardNode = root.querySelector('[data-compare-card]');
        const keyFeaturesNode = root.querySelector('[data-compare-key-features]');

        return {
          handle,
          title: root.getAttribute('data-product-title') || handle,
          size,
          material,
          cardNode: cardNode || document.createElement('div'),
          keyFeaturesHtml: keyFeaturesNode?.innerHTML || '—',
        };
      }

      /**
       * Size map → ordered label groups（兼容旧版扁平 JSON）
       * @param {unknown} sizeMap
       * @returns {Array<Record<string, string>>}
       */
      normalizeSizeMapGroups(sizeMap) {
        if (!sizeMap || typeof sizeMap !== 'object') return [];

        if (Array.isArray(sizeMap)) {
          return sizeMap
            .filter((group) => group && typeof group === 'object' && !Array.isArray(group))
            .map((group) => {
              /** @type {Record<string, string>} */
              const map = {};
              Object.entries(group).forEach(([key, value]) => {
                if (key === 'groups' || (value != null && typeof value === 'object')) return;
                map[key] = value == null ? '' : String(value);
              });
              return map;
            })
            .filter((group) => Object.keys(group).length > 0);
        }

        if (Array.isArray(sizeMap.groups)) {
          return this.normalizeSizeMapGroups(sizeMap.groups);
        }

        /** @type {Record<string, string>} */
        const flat = {};
        Object.entries(sizeMap).forEach(([key, value]) => {
          if (key === 'groups' || (value != null && typeof value === 'object')) return;
          flat[key] = value == null ? '' : String(value);
        });
        return Object.keys(flat).length > 0 ? [flat] : [];
      }

      /**
       * 收集参数字段，并按 size_map / material_map 的键顺序排序
       * @param {Array<{ size?: object, material?: object }>} products
       * @param {'size' | 'material'} group
       * @param {Record<string, string>} [orderMap]
       * @param {{ appendMissing?: boolean }} [opts]
       */
      collectParamKeys(products, group, orderMap = {}, opts = {}) {
        const appendMissing = opts.appendMissing !== false;
        const present = new Set();
        products.forEach((product) => {
          const data = product[group] || {};
          Object.keys(data).forEach((key) => {
            if (key !== 'display_name') present.add(key);
          });
        });

        const ordered = [];
        Object.keys(orderMap || {}).forEach((key) => {
          if (key === 'display_name' || !present.has(key)) return;
          ordered.push(key);
          present.delete(key);
        });
        if (appendMissing) {
          present.forEach((key) => ordered.push(key));
        }
        return ordered;
      }

      /**
       * Material 合并为一行：Material + ul/li 值列表
       * @param {string} label
       * @param {Array<object>} products
       * @param {Record<string, string>} materialMap
       * @param {string[]} materialKeys
       */
      buildMaterialMergedRow(label, products, materialMap, materialKeys) {
        const displayLabel = this.formatParamLabel(label);
        const tr = document.createElement('tr');
        tr.setAttribute('data-compare-material-row', '');

        const th = document.createElement('th');
        th.scope = 'row';
        th.className = 'product-compare-dialog__param';
        const thLabel = document.createElement('span');
        thLabel.className = 'product-compare-dialog__param-label';
        thLabel.textContent = displayLabel;
        th.appendChild(thLabel);
        tr.appendChild(th);

        products.forEach((product) => {
          const td = document.createElement('td');
          td.className = 'product-compare-dialog__value';

          const cellLabel = document.createElement('span');
          cellLabel.className = 'product-compare-dialog__param-label product-compare-dialog__param-label--cell';
          cellLabel.textContent = displayLabel;
          td.appendChild(cellLabel);

          const valueEl = document.createElement('span');
          valueEl.className = 'product-compare-dialog__param-value';

          const values = materialKeys
            .map((key) => {
              const raw = product.material?.[key];
              return raw != null ? String(raw).trim() : '';
            })
            .filter(Boolean);

          if (values.length === 0) {
            valueEl.textContent = '—';
          } else {
            const ul = document.createElement('ul');
            values.forEach((value) => {
              const li = document.createElement('li');
              li.textContent = value;
              ul.appendChild(li);
            });
            valueEl.appendChild(ul);
          }

          td.appendChild(valueEl);
          tr.appendChild(td);
        });

        return tr;
      }

      /**
       * @param {string} label
       * @param {Array<object>} products
       * @param {(product: object) => string | undefined} getValue
       * @param {boolean} [isHtml]
       * @param {boolean} [convertible] Size 行支持英制/公制切换
       */
      buildParamRow(label, products, getValue, isHtml = false, convertible = false) {
        const displayLabel = this.formatParamLabel(label);
        const tr = document.createElement('tr');
        if (convertible) tr.setAttribute('data-compare-size-row', '');

        const th = document.createElement('th');
        th.scope = 'row';
        th.className = 'product-compare-dialog__param';
        const thLabel = document.createElement('span');
        thLabel.className = 'product-compare-dialog__param-label';
        thLabel.textContent = displayLabel;
        th.appendChild(thLabel);
        tr.appendChild(th);

        products.forEach((product) => {
          const td = document.createElement('td');
          td.className = 'product-compare-dialog__value';

          const cellLabel = document.createElement('span');
          cellLabel.className = 'product-compare-dialog__param-label product-compare-dialog__param-label--cell';
          cellLabel.textContent = displayLabel;
          td.appendChild(cellLabel);

          const valueEl = document.createElement('span');
          valueEl.className = 'product-compare-dialog__param-value';

          const value = getValue(product);
          const hasValue = value != null && String(value).trim() !== '';
          if (isHtml) {
            valueEl.innerHTML = hasValue ? String(value) : '—';
          } else {
            valueEl.textContent = hasValue ? String(value) : '—';
          }

          if (convertible) {
            valueEl.setAttribute('data-compare-convertible', '');
            valueEl.setAttribute('data-original-html', valueEl.innerHTML);
          }

          td.appendChild(valueEl);
          tr.appendChild(td);
        });

        return tr;
      }

      /** @param {string} label */
      formatParamLabel(label) {
        const text = String(label || '').trim();
        if (!text) return '';
        return /[:：]$/.test(text) ? text : `${text}:`;
      }
    },
  );
}

